import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { useToast } from '../../hooks/use-toast';
import { Plus, RefreshCw } from 'lucide-react';
import { CALL_TYPES, labelFor, staffLabel, receptionApiErrorMessage } from './receptionConstants';
import { useReceptionStaff } from './useReceptionData';
import MobileInput from '../ui/mobile-input';
import DateTimeInput from '../ui/datetime-input';
import { validateMobile } from '../../utils/phoneValidation';
import { validateDateTime } from '../../utils/dateValidation';
import { dateTimeLocalValue } from '../../utils/appDateTime';

const empty = () => ({
  caller_name: '', phone_number: '', call_type: 'incoming', purpose: '', notes: '',
  call_datetime: dateTimeLocalValue(), staff_user_id: '',
});

export default function ReceptionPhoneCalls() {
  const { toast } = useToast();
  const { canManageFeature, hasPermission } = useAuth();
  const canManage = canManageFeature('reception') || hasPermission('manage_reception');
  const canDelete = hasPermission('delete_reception');
  const staff = useReceptionStaff();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [phoneError, setPhoneError] = useState('');
  const [datetimeError, setDatetimeError] = useState('');

  const fetchItems = async () => {
    setLoading(true);
    try {
      const res = await api.get('/reception/phone-calls');
      setItems(res.data?.data?.items || []);
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const save = async () => {
    const err = validateMobile(form.phone_number, { required: false });
    if (err) {
      setPhoneError(err);
      toast({ title: 'Invalid mobile number', description: err, variant: 'destructive' });
      return;
    }
    const dtErr = validateDateTime(form.call_datetime, { required: false });
    if (dtErr) {
      setDatetimeError(dtErr);
      toast({ title: 'Invalid date and time', description: dtErr, variant: 'destructive' });
      return;
    }
    setPhoneError('');
    setDatetimeError('');
    try {
      await api.post('/reception/phone-calls', form);
      toast({ title: 'Call logged' });
      setOpen(false);
      setForm(empty());
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete call log?')) return;
    try {
      await api.delete(`/reception/phone-calls/${id}`);
      toast({ title: 'Deleted' });
      fetchItems();
    } catch (e) {
      toast({ title: 'Error', description: receptionApiErrorMessage(e), variant: 'destructive' });
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div><h1 className="text-2xl font-bold">Phone Call Log</h1><p className="text-muted-foreground">Incoming and outgoing calls</p></div>
        <div className="flex gap-2">
          <Button variant="outline" size="icon" onClick={fetchItems}><RefreshCw className="w-4 h-4" /></Button>
          {canManage && <Button onClick={() => setOpen(true)}><Plus className="w-4 h-4 mr-2" />Log Call</Button>}
        </div>
      </div>
      <Card>
        <CardHeader><CardTitle>Call Records</CardTitle></CardHeader>
        <CardContent>
          {loading ? <p className="text-muted-foreground">Loading…</p> : (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-muted-foreground">
                <th className="py-2">Caller</th><th className="py-2">Phone</th><th className="py-2">Type</th>
                <th className="py-2">Purpose</th><th className="py-2">Staff</th><th className="py-2">Date</th><th className="py-2">Actions</th>
              </tr></thead>
              <tbody>
                {items.map((r) => (
                  <tr key={r.id} className="border-b border-border/50">
                    <td className="py-2">{r.caller_name || '—'}</td>
                    <td className="py-2">{r.phone_number || '—'}</td>
                    <td className="py-2">{labelFor(CALL_TYPES, r.call_type)}</td>
                    <td className="py-2">{r.purpose || '—'}</td>
                    <td className="py-2">{staffLabel({ first_name: r.staff_first_name, last_name: r.staff_last_name })}</td>
                    <td className="py-2">{r.call_datetime || '—'}</td>
                    <td className="py-2">{canDelete && <Button size="sm" variant="destructive" onClick={() => remove(r.id)}>Delete</Button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Log Phone Call</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Caller Name</Label><Input value={form.caller_name} onChange={(e) => setForm({ ...form, caller_name: e.target.value })} /></div>
            <div><Label>Phone</Label><MobileInput value={form.phone_number} onChange={(v) => { setForm({ ...form, phone_number: v }); setPhoneError(''); }} error={phoneError} /></div>
            <div><Label>Call Type</Label>
              <Select value={form.call_type} onValueChange={(v) => setForm({ ...form, call_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CALL_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Staff Member</Label>
              <Select value={form.staff_user_id || 'none'} onValueChange={(v) => setForm({ ...form, staff_user_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Self</SelectItem>
                  {staff.map((s) => <SelectItem key={s.id} value={String(s.id)}>{staffLabel(s)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Purpose</Label><Input value={form.purpose} onChange={(e) => setForm({ ...form, purpose: e.target.value })} /></div>
            <div><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
            <div><Label>Date & Time</Label><DateTimeInput value={form.call_datetime} onChange={(v) => { setForm({ ...form, call_datetime: v }); setDatetimeError(''); }} error={datetimeError} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
